<template>
  <div>
    <splide
      ref="splide"
      :options="splideOptions"
      :has-track="false"
      class="bracelet-finder__splide splide--pagination-reset"
    >
      <splide-track>
        <template
          v-for="(bracelet, index) of sortedBracelets"
          :key="`slide-${index}`"
        >
          <splide-slide
            :data-metal="bracelet.metal_type"
            :data-slide="`slide-${index}`"
          >
            <responsive-image
              :image="bracelet.image"
              min-max="275-600"
              ratio="1:1"
              sizes="300px"
            />
          </splide-slide>
        </template>
      </splide-track>

      <div class="splide__arrows splide__arrows--ltr">
        <button
          type="button"
          class="splide__arrow splide__arrow--prev"
          :aria-label="$string('accessibility.carousel.previous')"
          disabled
        >
          <icon-arrow-left />
        </button>

        <button
          type="button"
          class="splide__arrow splide__arrow--next"
          :aria-label="$string('accessibility.carousel.next')"
        >
          <icon-arrow-right />
        </button>
      </div>
    </splide>

    <div class="bracelet-finder__magnify-nav-container">
      <div class="bracelet-finder__magnify-nav" />
    </div>

    <div class="bracelet-finder__slider-nav">
      <splide
        ref="splideNav"
        :options="splideNavOptions"
        :has-track="false"
        class="bracelet-finder__splide-nav"
      >
        <splide-track>
          <template
            v-for="(metal, index) of sortedBracelets"
            :key="metal.metal_type"
          >
            <splide-slide
              :data-metal="metal.metal_type"
              :data-nav-slide="`slide-${index}`"
            >
              <button
                :aria-label="`Select ${metal.metal_type}`"
                type="button"
                @click="handleSlideNavigation(index)"
              >
                {{ metal.metal_type }}
              </button>
            </splide-slide>
          </template>
        </splide-track>
      </splide>
    </div>

    <div class="bracelet-finder__actions">
      <btn
        :label="$string('bracelet_finder.confirm')"
        modifiers="primary"
        class="bracelet-finder__confirm"
        @click="handleConfirmation"
      />
    </div>
  </div>
</template>

<script>

/**
* Vue: Bracelet finder step 1 (bracelet-finder-step-1)
* -----------------------------------------------------------------------------
*
*
*/

import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'BraceletFinderStepOne',

  components: {
    IconArrowLeft,
    IconArrowRight,
    Splide,
    SplideSlide,
    SplideTrack,
    Btn,
    ResponsiveImage,
  },

  props: {
    bracelets: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'confirm-step': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      activeSlideIndex: 0,
      splideOptions: {
        mediaQuery: 'min',
        flickMaxPages: 1,
        focus: 'center',
        gap: 'var(--spacing-m)',
        perMove: 1,
        perPage: 1.15,
        wheel: false,
        arrows: false,
        drag: true,
        flickPower: 200,
        pagination: false,
        type: 'slide',
        omitEnd: true,
        autoplay: false,
        respectReducedMotion: true,
        trimSpace: false,
        fixedWidth: '275px',
        fixedHeight: '275px',
        updateOnMove: true,
        breakpoints: {
          [breakpoints.m]: {
            gap: 'var(--spacing-xl)',
            perPage: 1,
          },
          [breakpoints.l]: {
            gap: '226px',
            keyboard: true,
            perMove: 1,
            perPage: 3,
            pagination: false,
            trimSpace: false,
            fixedWidth: '300px',
            fixedHeight: '300px',
            arrows: true,
          },
        },
      },
      splideNavOptions: {
        mediaQuery: 'max',
        focus: 'center',
        wheel: false,
        arrows: false,
        drag: false,
        flickPower: 200,
        perPage: this.bracelets.length,
        pagination: false,
        type: 'slide',
        omitEnd: true,
        autoplay: false,
        respectReducedMotion: true,
        keyboard: true,
        trimSpace: false,
        fixedWidth: '95px',
        breakpoints: {
          [breakpoints.m]: {
            perPage: 3,
            drag: true,
          },
        },
      },
    }
  },

  computed: {
    sortedBracelets() {
      const braceletsToBeSorted = this.bracelets.slice()
      return braceletsToBeSorted.sort((a, b) => a.order - b.order)
    },
  },

  mounted() {
    this.$refs.splide.splide.on('moved', (newIndex) => {
      this.activeSlideIndex = newIndex
      this.$refs.splideNav.go(newIndex)
    })
  },

  methods: {
    handleSlideNavigation(index) {
      this.$refs.splide.go(index)
      this.$refs.splideNav.go(index)
    },

    handleConfirmation() {
      const activeSlide = this.$refs.splide.splide.Components.Elements.slides[this.activeSlideIndex]
      const selectedMetal = activeSlide.dataset.metal
      const confirmationObject = {
        selection: selectedMetal,
        step: 1,
      }
      this.setNewUrl(selectedMetal)
      this.$emit('confirm-step', confirmationObject)
    },

    setNewUrl(selectedMetal) {
      const urlParameters = new URLSearchParams(location.search)

      urlParameters.forEach((value, key) => urlParameters.delete(key))
      urlParameters.set('step', '2')
      urlParameters.set('metal', selectedMetal)

      const url = `${location.origin}${location.pathname}?${urlParameters.toString()}`
      history.pushState({
        html: '',
      }, '', url)
    },
  },
}
</script>
